import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";
import urunSynonyms from "../data/urunadi-es-anlamlar.json";

/**
 * OnarimKararDestek (joker destekli)
 *
 * İstekler:
 * - Faturalar araması: Ürün Adı girişine göre "içerir" arama. Joker destekli:
 *    - "kabin hortum***"  => "kabin hortum" ile başlayan tüm ürün adları
 *    - "*** civata"       => "civata" ile biten tüm ürün adları
 *    - "filtre***civata"  => içinde sıralı olarak "filtre" sonra "civata" geçenler
 * - İki tablo: (1) Girilen plakaya ait olanlar (2) Plaka sınırsız tüm kayıtlar
 * - Firma filtresi YOK.
 * - Issues araması: Varsayılan Ürün Adı metni ile jokerli içerir arama;
 *   "Etiketten ara (JSON)" seçilirse, kanonik etiketi (json anahtar) kullan.
 * - Tasarım: sade kartlar, özet kutuları, tablolar.
 */

// ----------------- Yardımcılar -----------------
const trLower = (s = "") => s.toLocaleLowerCase("tr-TR");

function normalizeStr(s) {
  if (!s) return "";
  return trLower(s)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ş/g, "s")
    .replace(/ı/g, "i").replace(/ö/g, "o").replace(/ç/g, "c")
    .trim();
}

const stripNonAlnumASCII = (s) => (s || "").replace(/[^a-z0-9]/g, "");

function toNumberSafe(val) {
  if (val === null || val === undefined || val === "") return null;
  if (typeof val === "number") return Number.isFinite(val) ? val : null;
  const normalized = String(val).replace(/\s+/g, "").replace(/,/g, ".");
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

function toMillis(t) {
  if (!t) return 0;
  if (typeof t === "number") return t;
  if (t?.seconds) return t.seconds * 1000 + (t.nanoseconds ? Math.floor(t.nanoseconds / 1e6) : 0);
  const d = new Date(t);
  const n = d.getTime();
  return Number.isFinite(n) ? n : 0;
}

function formatDateTR(t) {
  if (!t) return "-";
  return new Date(toMillis(t)).toLocaleDateString("tr-TR");
}

function formatTRY(n) {
  if (!Number.isFinite(n)) return "-";
  try { return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(n); }
  catch { return `${n.toFixed(2)} ₺`; }
}

// Kanonik etiket bul (Issues için kullanılacak)
function findCanonical(input) {
  if (!input) return "";
  const n = normalizeStr(input);
  for (const canonical of Object.keys(urunSynonyms)) {
    if (normalizeStr(canonical) === n) return canonical;
    const aliases = urunSynonyms[canonical] || [];
    for (const a of aliases) {
      const an = normalizeStr(a);
      if (an === n || an.includes(n) || n.includes(an)) return canonical;
    }
  }
  return input; // eşanlam listesinde yoksa girdiyi olduğu gibi kullan
}

// --- Jokerli eşleşme ---
// Örnekler: "kabin hortum***" (prefix), "*** civata" (suffix), "filtre***civata" (ordered contains)
function makeMatcher(patternRaw) {
  const raw = (patternRaw || "").trim();
  if (!raw) return () => false;
  const norm = normalizeStr(raw);
  const startsWithStar = norm.startsWith("***");
  const endsWithStar = norm.endsWith("***");
  const segments = norm.split("***").map((s) => stripNonAlnumASCII(normalizeStr(s))).filter(Boolean);
  const hasWildcard = norm.includes("***");

  return (text) => {
    const uNorm = stripNonAlnumASCII(normalizeStr(text || ""));
    if (!uNorm) return false;

    if (!hasWildcard) {
      // düz içerir
      const q = stripNonAlnumASCII(normalizeStr(norm));
      return q ? uNorm.includes(q) : false;
    }

    if (segments.length === 0) return false;

    if (segments.length === 1) {
      const s = segments[0];
      if (startsWithStar && !endsWithStar) return uNorm.endsWith(s);   // ***suffix
      if (!startsWithStar && endsWithStar) return uNorm.startsWith(s); // prefix***
      return uNorm.includes(s); // ***middle***
    }

    // Ordered contains ("a***b***c"): a sonra b sonra c sırayla geçsin
    let pos = 0;
    // Prefix zorunluluğu: ilk segment başta olmalı mı?
    if (!startsWithStar) {
      if (!uNorm.startsWith(segments[0])) return false;
      pos = segments[0].length;
    }
    for (let i = startsWithStar ? 0 : 1; i < segments.length; i++) {
      const seg = segments[i];
      const idx = uNorm.indexOf(seg, pos);
      if (idx === -1) return false;
      pos = idx + seg.length;
    }
    // Suffix zorunluluğu: son segment sonda bitmeli mi?
    if (!endsWithStar) {
      const last = segments[segments.length - 1];
      if (!uNorm.endsWith(last)) return false;
    }
    return true;
  };
}

// ----------------- Bileşen -----------------
function OnarimKararDestek() {
  const [urunAdi, setUrunAdi] = useState("");
  const [plaka, setPlaka] = useState("");
  const [fiyat, setFiyat] = useState("");
  const [etikettenAra, setEtikettenAra] = useState(false); // sadece Issues için

  const [firmaOptions, setFirmaOptions] = useState([]); // sadece autocomplete için (görsel)
  const [plakaOptions, setPlakaOptions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [rowsAll, setRowsAll] = useState([]); // tüm plakalar
  const [rowsPlate, setRowsPlate] = useState([]); // sadece girilen plaka
  const [statsAll, setStatsAll] = useState({ count: 0, min: null, max: null, avg: null, med: null });
  const [statsPlate, setStatsPlate] = useState({ count: 0, min: null, max: null, avg: null, med: null });
  const [issueInfo, setIssueInfo] = useState(null);

  // Autocomplete kaynakları (sadece görüntü)
  useEffect(() => {
    (async () => {
      try {
        const [firmaSnap, plakaSnap] = await Promise.all([
          getDocs(collection(db, "firms")),
          getDocs(collection(db, "plates")),
        ]);
        const firmalar = firmaSnap.docs
          .map((d) => d.data())
          .filter((x) => x?.active !== false && x?.firmaAdi)
          .map((x) => String(x.firmaAdi))
          .sort((a, b) => a.localeCompare(b, "tr"));
        setFirmaOptions(firmalar);

        const plaks = plakaSnap.docs
          .map((d) => d.data()?.plate)
          .filter(Boolean)
          .map(String)
          .sort();
        setPlakaOptions(plaks);
      } catch (e) {
        console.error("Autocomplete yüklenemedi", e);
      }
    })();
  }, []);

  const canonical = useMemo(() => findCanonical(urunAdi), [urunAdi]);

  const handleFiyatChange = (e) => {
    const val = e.target.value;
    setFiyat(val.replace(/[^0-9.,]/g, ""));
  };

  const handleSorgula = async () => {
    setLoading(true);
    setRowsAll([]);
    setRowsPlate([]);
    setStatsAll({ count: 0, min: null, max: null, avg: null, med: null });
    setStatsPlate({ count: 0, min: null, max: null, avg: null, med: null });
    setIssueInfo(null);

    try {
      // -------- Faturalar (joker destekli içerir) --------
      const fSnap = await getDocs(query(collection(db, "faturalar"), where("deleted", "==", false)));
      const all = [];
      const match = makeMatcher(urunAdi);

      fSnap.forEach((docSnap) => {
        const d = docSnap.data();
        const firmaAdi = d?.firmaAdi || "";
        const docTarih = d?.tarih;
        const satirlar = Array.isArray(d?.satirlar) ? d.satirlar : [];

        satirlar.forEach((s) => {
          const u = s?.urunAdi;
          if (!u) return;
          if (!match(u)) return;

          const f = toNumberSafe(s?.fiyat);
          const p = s?.plaka ? String(s.plaka) : "";
          const rowTarih = s?.tarih ?? docTarih;
          all.push({
            firma: String(firmaAdi || "-"),
            tarih: rowTarih,
            urunAdi: String(u),
            fiyat: f,
            plaka: p || "-",
          });
        });
      });

      // Sıralama (tarih artan) ve artış bayrağı
      const allSorted = [...all].sort((a, b) => toMillis(a.tarih) - toMillis(b.tarih));
      const withIncAll = addIncreaseFlags(allSorted);
      const onlyPlate = plaka ? withIncAll.filter((r) => normalizeStr(r.plaka) === normalizeStr(plaka)) : [];
      const withIncPlate = addIncreaseFlags(onlyPlate);

      setRowsAll(withIncAll);
      setRowsPlate(withIncPlate);
      setStatsAll(calcStats(withIncAll));
      setStatsPlate(calcStats(withIncPlate));

      // -------- Issues --------
      let latestIssue = null;
      const termForIssues = etikettenAra ? canonical : urunAdi; // toggle
      const issueMatch = makeMatcher(termForIssues);
      const iSnap = await getDocs(query(collection(db, "issues"), where("resolved", "==", true)));
      iSnap.forEach((docSnap) => {
        const d = docSnap.data();
        const issueText = d?.issue || "";
        const issuePlate = d?.plate || "";
        const resolvedAt = d?.resolvedAt;
        const issueDate = d?.date;

        if (plaka && normalizeStr(issuePlate) !== normalizeStr(plaka)) return;
        if (!issueMatch(issueText)) return;

        if (!latestIssue || toMillis(resolvedAt) > toMillis(latestIssue.resolvedAt)) {
          latestIssue = { plate: issuePlate, resolvedAt, issueText, issueDate };
        }
      });
      setIssueInfo(latestIssue);
    } catch (e) {
      console.error("Sorgu hatası", e);
    } finally {
      setLoading(false);
    }
  };

  const teklifKiyas = useMemo(() => {
    const teklif = toNumberSafe(fiyat);
    const base = statsPlate?.count ? statsPlate : statsAll;
    if (!base?.count || !Number.isFinite(teklif)) return null;
    const diffPct = ((teklif - base.max) / base.max) * 100;
    return {
      teklif,
      diffPct,
      belowMin: base.min != null ? teklif < base.min : false,
      aboveMax: base.max != null ? teklif > base.max : false,
    };
  }, [fiyat, statsAll, statsPlate]);

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: "0 auto" }}>
      <h2 style={{ fontSize: 22, fontWeight: "bold", color: "#334155", marginBottom: 16 }}>Onarım Karar Destek</h2>

      {/* Arama Alanları */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", alignItems: "start", gap: 10, marginBottom: 12 }}>
        <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>Ürün Adı (joker destekli)</div>
          <input
            placeholder="Örn: kabin hortum***, *** civata, filtre***civata"
            value={urunAdi}
            onChange={(e) => setUrunAdi(e.target.value)}
            style={{ padding: 8, border: "1px solid #cbd5e1", borderRadius: 8, width: "100%" }}
          />
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 4, overflowWrap: "anywhere" }}>
            <b>***</b> joker: başa yazılırsa sonu uyumlu, sona yazılırsa başı uyumlu, araya yazılırsa sıralı içerir.
          </div>
        </div>
        <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 4, marginLeft: 20 }}>Plaka (opsiyonel)</div>
          <input list="plakalar" placeholder="Örn: 06AR0505" value={plaka} onChange={(e) => setPlaka(e.target.value.toUpperCase())} style={{ padding: 8, border: "1px solid #cbd5e1", borderRadius: 8, width: "100%", marginLeft: 20 }} />
          <datalist id="plakalar">{plakaOptions.map((p) => (<option key={p} value={p} />))}</datalist>
        </div>
        <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 4, marginLeft: 40 }}>Teklif Fiyatı (ops.)</div>
          <input inputMode="decimal" placeholder="Örn: 1250,00" value={fiyat} onChange={handleFiyatChange} style={{ padding: 8, border: "1px solid #cbd5e1", borderRadius: 8, width: "100%", marginLeft: 40 }} />
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 4, marginLeft: 40 }}>Sadece sayı/virgül/nokta.</div>
        </div>
        <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 4, marginLeft: 60 }}>
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>Eksiklik araması</div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={etikettenAra} onChange={(e) => setEtikettenAra(e.target.checked)} />
            Etiketten ara (JSON kanonik)
          </label>
          <div style={{ fontSize: 11, color: "#64748b" }}>Seçili değilse issues aramasında girilen ifade kullanılır.</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 12 }}>
        <button onClick={handleSorgula} disabled={loading || !urunAdi.trim()} style={{ backgroundColor: "#111827", color: "white", padding: "8px 16px", borderRadius: 10, border: "none", cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.1)" }}>
          {loading ? "Sorgulanıyor…" : "Sorgula"}
        </button>
        <span style={{ fontSize: 12, color: "#64748b" }}>Uygunsuzluk etiketi: <b>{etikettenAra ? canonical || "-" : "(girdi kullanılacak)"}</b></span>
      </div>

      {/* Teklif Kıyas */}
      {teklifKiyas && (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#f8fafc", marginBottom: 16 }}>
          <div style={{ fontWeight: "bold", marginBottom: 6 }}>Teklif Kıyas</div>
          <div>Teklif: <b>{formatTRY(teklifKiyas.teklif)}</b></div>
          <div style={{ marginTop: 4 }}>En yüksek fiyata göre fark: <b>{teklifKiyas.diffPct.toFixed(1)}%</b>
            {teklifKiyas.belowMin && <span style={{ marginLeft: 8, color: "#16a34a" }}>(Min'in altında)</span>}
            {teklifKiyas.aboveMax && <span style={{ marginLeft: 8, color: "#dc2626" }}>(Max'ın üstünde)</span>}
          </div>
        </div>
      )}

      {/* Plakaya Ait Kayıtlar */}
      <Section title="Bu Plakaya Ait Kayıtlar">
        {plaka ? (
          <>
            <StatsRow stats={statsPlate} />
            <DataTable rows={rowsPlate} />
            {rowsPlate.length === 0 && !loading && <EmptyState text="Bu plakada kayıt bulunamadı." />}
          </>
        ) : (
          <EmptyState text="Plaka seçilmedi." />
        )}
      </Section>

      {/* Tüm Plakalar */}
      <Section title="Tüm Plakalarda Kayıtlar">
        <StatsRow stats={statsAll} />
        <DataTable rows={rowsAll} />
        {rowsAll.length === 0 && !loading && <EmptyState text="Kayıt bulunamadı." />}
      </Section>

      {/* Issues */}
      <Section title="Son Çözüm Durumu">
        {issueInfo ? (
          <YakinTarihBilgisi plate={issueInfo.plate} issueText={issueInfo.issueText} issueDate={issueInfo.issueDate} resolvedAt={issueInfo.resolvedAt} />
        ) : (
          <EmptyState text="Uygun bir çözüm kaydı bulunamadı." />
        )}
      </Section>
    </div>
  );
}

// ----------------- Alt Bileşenler & Hesaplayıcılar -----------------
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ height: 18, width: 6, borderRadius: 6, background: "#111827" }} />
        <h3 style={{ fontSize: 16, fontWeight: "bold" }}>{title}</h3>
      </div>
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#ffffff" }}>{children}</div>
    </div>
  );
}

function StatsRow({ stats }) {
  const cards = [
    { k: "min", label: "En Düşük", fmt: (v) => formatTRY(v) },
   // { k: "med", label: "Medyan", fmt: (v) => formatTRY(v) },
    { k: "avg", label: "Ortalama", fmt: (v) => formatTRY(v) },
    { k: "max", label: "En Yüksek", fmt: (v) => formatTRY(v) },
    { k: "count", label: "Adet", fmt: (v) => (v ?? "-") },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(140px, 1fr))", gap: 10, marginBottom: 12 }}>
      {cards.map(({ k, label, fmt }) => (
        <div key={k} style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 10, background: "#f8fafc" }}>
          <div style={{ fontSize: 12, color: "#475569" }}>{label}</div>
          <div style={{ fontWeight: "bold", marginTop: 4 }}>{fmt(stats?.[k])}</div>
        </div>
      ))}
    </div>
  );
}

function DataTable({ rows }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 900, fontSize: 13 }}>
        <thead style={{ background: "#e2e8f0" }}>
          <tr>
            {["Tarih", "Firma", "Plaka", "Ürün", "Fiyat"].map((h) => (
              <th key={h} style={{ border: "1px solid #cbd5e1", padding: "6px 8px", textAlign: "left" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows?.length ? (
            rows.map((r, idx) => (
              <tr key={idx} style={{ background: idx % 2 ? "#f8fafc" : "white" }}>
                <td style={{ border: "1px solid #e5e7eb", padding: "6px 8px" }}>{formatDateTR(r.tarih)}</td>
                <td style={{ border: "1px solid #e5e7eb", padding: "6px 8px" }}>{r.firma}</td>
                <td style={{ border: "1px solid #e5e7eb", padding: "6px 8px" }}>{r.plaka}</td>
                <td style={{ border: "1px solid #e5e7eb", padding: "6px 8px", maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.urunAdi}</td>
                <td style={{ border: "1px solid #e5e7eb", padding: "6px 8px", fontWeight: r._inc ? "bold" : "normal", color: r._inc ? "#dc2626" : "inherit" }} title={r._inc ? "Bir önceki satıra göre fiyat artmış" : ""}>
                  {formatTRY(toNumberSafe(r.fiyat))}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={5} style={{ padding: 10, color: "#475569" }}>—</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState({ text }) {
  return <div style={{ fontSize: 13, color: "#64748b" }}>{text}</div>;
}

function YakinTarihBilgisi({ plate, issueText, issueDate, resolvedAt }) {
  const msResolved = toMillis(resolvedAt);
  const msIssue = toMillis(issueDate);
  const daysAgo = Number.isFinite(msResolved) ? Math.floor((Date.now() - msResolved) / (1000 * 60 * 60 * 24)) : null;
  const duration = Number.isFinite(msResolved) && Number.isFinite(msIssue)
    ? Math.max(0, Math.round((msResolved - msIssue) / (1000 * 60 * 60 * 24)))
    : null;
  const yakin = daysAgo != null && daysAgo <= 45; // 45 gün eşiği
  const text = issueText || "ilgili iş";
  return (
    <div style={{ fontSize: 13, color: yakin ? "#dc2626" : "#111827" }}>
      {plate ? `${plate} plakalı aracın ` : ""}<b>{text}</b> {formatDateTR(resolvedAt)} tarihinde çözülmüştür.
      {typeof duration === "number" && <span> Çözülme süresi: <b>{duration} gün</b>.</span>}
      {daysAgo != null && <span> ({daysAgo} gün önce)</span>}
      {yakin && <span style={{ marginLeft: 6, fontWeight: 600 }}>→ Yakın tarihte çözüm var!</span>}
    </div>
  );
}

function calcStats(arr) {
  const xs = (arr || []).map((r) => toNumberSafe(r.fiyat)).filter((n) => Number.isFinite(n));
  const n = xs.length;
  const min = n ? Math.min(...xs) : null;
  const max = n ? Math.max(...xs) : null;
  const avg = n ? xs.reduce((a, b) => a + b, 0) / n : null;
  const med = n ? (() => { const s = [...xs].sort((a, b) => a - b); return n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2; })() : null;
  return { count: (arr || []).length, min, max, avg, med };
}

function addIncreaseFlags(arr) {
  let prev = null;
  return (arr || []).map((r) => {
    const f = toNumberSafe(r.fiyat);
    const _inc = prev != null ? (Number.isFinite(f) && f > prev) : false;
    prev = Number.isFinite(f) ? f : prev;
    return { ...r, _inc };
  });
}

export default OnarimKararDestek;
